import { getOwner } from '@ember/application';

/**
 * Wraps a GraphQL query with retry logic
 * @param {Object} context - The route or component context
 * @param {Function} queryFn - Function that returns the GraphQL query result
 * @param {Object} options - Retry configuration
 * @returns {Promise<any>} Query result with retry logic
 */
export async function withGraphQLRetry(context, queryFn, options = {}) {
  const owner = getOwner(context);
  const networkRetryService = owner.lookup('service:network-retry');

  const defaultOptions = {
    maxRetries: 2,
    baseDelay: 500,
    maxDelay: 5000,
    backoffFactor: 2,
    onRetry: (error, attempt, delay) => {
      console.warn(
        `GraphQL query retry attempt ${attempt} after ${Math.round(delay)}ms:`,
        error.message,
      );
    },
  };

  return networkRetryService.retryGraphQL(
    queryFn,
    {},
    { ...defaultOptions, ...options },
  );
}

/**
 * Enhanced model hook for routes that includes retry logic
 * @param {Object} route - The route context
 * @param {Function} queryPromise - Function that returns the query promise
 * @param {Function} transformFn - Function to transform successful results
 * @param {any} fallbackValue - Value to return on failure
 * @param {Object} retryOptions - Retry configuration
 * @returns {Promise<any>} Model data with retry logic
 */
export async function retryGraphQLModel(
  route,
  queryPromise,
  transformFn = null,
  fallbackValue = [],
  retryOptions = {},
) {
  try {
    const result = await withGraphQLRetry(route, queryPromise, retryOptions);

    return transformFn ? transformFn(result) : result;
  } catch (error) {
    console.error('GraphQL query failed after retries:', error);

    // Check if we should redirect on certain errors
    if (error.status === 404 && route.router) {
      route.router.replaceWith('404');
      return null;
    }

    return fallbackValue;
  }
}

/**
 * Creates a retry-enabled query executor for useQuery with glimmer-apollo
 * @param {Object} context - The component or route context
 * @param {Object} queryObject - The query object from useQuery
 * @param {Object} options - Retry configuration
 * @returns {Object} Enhanced query object with retry capabilities
 */
export function enhanceQueryWithRetry(context, queryObject, options = {}) {
  const owner = getOwner(context);
  const networkRetryService = owner.lookup('service:network-retry');

  // Store original refetch method
  const originalRefetch = queryObject.refetch?.bind(queryObject);

  // Create enhanced refetch with retry logic
  const retryRefetch = async (variables = {}) => {
    return networkRetryService.withRetry(
      async () => {
        if (originalRefetch) {
          return originalRefetch(variables);
        } else {
          // Fallback to promise-based refetch
          return queryObject.promise;
        }
      },
      {
        maxRetries: options.maxRetries || 2,
        baseDelay: options.baseDelay || 500,
        maxDelay: options.maxDelay || 5000,
        onRetry: (error, attempt, delay) => {
          console.warn(
            `Query refetch retry attempt ${attempt} after ${Math.round(
              delay,
            )}ms:`,
            error.message,
          );
          if (options.onRetry) {
            options.onRetry(error, attempt, delay);
          }
        },
      },
    );
  };

  // Return enhanced query object
  return {
    ...queryObject,
    refetch: retryRefetch,
    retryableRefetch: retryRefetch,
  };
}

/**
 * Creates a manual retry function for failed queries
 * @param {Object} context - The component or route context
 * @param {Function} queryFn - Function to execute the query
 * @param {Object} options - Retry configuration
 * @returns {Function} Manual retry function
 */
export function createManualRetry(context, queryFn, options = {}) {
  return async () => {
    return withGraphQLRetry(context, queryFn, {
      maxRetries: options.maxRetries || 3,
      baseDelay: options.baseDelay || 1000,
      ...options,
    });
  };
}

/**
 * Batch retry utility for multiple GraphQL operations
 * @param {Object} context - The component or route context
 * @param {Array} operations - Array of operation functions
 * @param {Object} options - Retry configuration
 * @returns {Promise<Array>} Results of all operations
 */
export async function batchGraphQLRetry(context, operations, options = {}) {
  const owner = getOwner(context);
  const networkRetryService = owner.lookup('service:network-retry');

  const promises = operations.map((operation, index) =>
    networkRetryService.withRetry(operation, {
      ...options,
      operationId: `batch-${Date.now()}-${index}`,
    }),
  );

  if (options.parallel !== false) {
    return Promise.all(promises);
  } else {
    // Sequential execution
    const results = [];
    for (const promise of promises) {
      results.push(await promise);
    }
    return results;
  }
}
